import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CreditCard, Loader2, Check, X, FileImage, ShieldAlert, ClipboardList, PenLine, Paperclip, Receipt as ReceiptIcon,
} from "lucide-react";
import api, { API, formatApiError } from "../lib/api";
import { formatFCFA, formatDateTime, ROLE_LABELS } from "../lib/format";
import { PageHeader, Spinner, StatusBadge, MethodBadge, EmptyState } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const EVENT_ICON = { created: PenLine, proof_attached: Paperclip, internal_receipt: ReceiptIcon, approved: Check, rejected: X };

function Timeline({ events }) {
  return (
    <div className="space-y-0">
      {events.map((e, i) => {
        const Icon = EVENT_ICON[e.event] || ClipboardList;
        const color = e.event === "approved" ? "text-emerald-600 bg-emerald-50" : e.event === "rejected" ? "text-rose-600 bg-rose-50" : "text-slate-500 bg-slate-100";
        return (
          <div key={i} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full ${color}`}><Icon className="h-4 w-4" /></div>
              {i < events.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
            </div>
            <div className="pb-5">
              <p className="text-sm font-medium text-slate-800">{e.label}</p>
              <p className="text-xs text-slate-400">{e.by_name} · {ROLE_LABELS[e.by_role] || e.by_role} · {formatDateTime(e.at)}</p>
              {e.reason && <p className="mt-1 text-xs text-slate-600 bg-slate-50 rounded px-2 py-1 italic">« {e.reason} »</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PaymentDialog({ paymentId, onClose, onChanged }) {
  const { user } = useAuth();
  const isPrincipal = user?.role === "principal_admin";
  const [p, setP] = useState(null);
  const [reason, setReason] = useState("");
  const [acting, setActing] = useState(false);

  const load = useCallback(() => {
    if (paymentId) api.get(`/payments/${paymentId}`).then((r) => { setP(r.data); setReason(""); }).catch(() => {});
  }, [paymentId]);
  useEffect(() => { load(); }, [load]);

  const decide = async (action) => {
    if (action === "reject" && !reason.trim()) { toast.error("Un motif de rejet est obligatoire"); return; }
    setActing(true);
    try {
      const { data } = await api.post(`/payments/${paymentId}/${action}`, { reason: reason || null });
      toast.success(action === "approve" ? `Paiement valide — ${data.receipt_number}` : "Paiement rejete");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActing(false);
    }
  };

  return (
    <Dialog open={!!paymentId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-4">
            <span>Dossier de paiement</span>
            {p && <StatusBadge status={p.status} />}
          </DialogTitle>
          <DialogDescription>Justificatif, historique d'audit et decision de validation.</DialogDescription>
        </DialogHeader>
        {!p ? <Spinner /> : (
          <>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="font-mono text-2xl font-bold text-slate-900">{formatFCFA(p.amount)}</p>
                  <div className="mt-2 flex items-center gap-2"><MethodBadge method={p.method} /></div>
                  <dl className="mt-4 space-y-1.5 text-sm">
                    <div className="flex justify-between"><dt className="text-slate-500">Client</dt><dd className="font-medium">{p.customer?.name}</dd></div>
                    <div className="flex justify-between"><dt className="text-slate-500">Facture</dt><dd className="font-mono">{p.invoice?.number}</dd></div>
                    <div className="flex justify-between"><dt className="text-slate-500">Reference</dt><dd>{p.reference || "-"}</dd></div>
                    {p.internal_receipt_number && <div className="flex justify-between"><dt className="text-slate-500">Recu interne</dt><dd className="font-mono">{p.internal_receipt_number}</dd></div>}
                    {p.receipt_number && <div className="flex justify-between"><dt className="text-slate-500">Recu officiel</dt><dd className="font-mono text-emerald-600">{p.receipt_number}</dd></div>}
                    <div className="flex justify-between"><dt className="text-slate-500">Prepare par</dt><dd>{p.prepared_by_name}</dd></div>
                  </dl>
                </div>

                {p.proof_file_id ? (
                  <div>
                    <p className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><FileImage className="h-4 w-4" /> Justificatif</p>
                    <a href={`${API}/files/${p.proof_file_id}`} target="_blank" rel="noreferrer" data-testid="proof-link" className="block">
                      <img src={`${API}/files/${p.proof_file_id}`} alt="justificatif" className="w-full rounded-lg border border-slate-200 max-h-64 object-contain bg-slate-50"
                        onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                      <span style={{ display: "none" }} className="items-center gap-2 rounded-lg border border-slate-200 px-4 py-6 text-sm text-teal-600 justify-center">
                        <FileImage className="h-4 w-4" /> Ouvrir le justificatif (PDF)
                      </span>
                    </a>
                  </div>
                ) : p.method === "cash" ? (
                  <p className="text-xs text-emerald-700 bg-emerald-50 rounded-md px-3 py-2">Paiement en especes — recu interne {p.internal_receipt_number} genere.</p>
                ) : null}
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-800 mb-3">Historique & audit</p>
                <Timeline events={p.timeline || []} />
              </div>
            </div>

            {p.status === "pending" && (
              isPrincipal ? (
                <div className="border-t border-slate-100 pt-4 mt-2 space-y-3">
                  <Textarea data-testid="decision-reason" placeholder="Motif de la decision (obligatoire en cas de rejet)" value={reason} onChange={(e) => setReason(e.target.value)} />
                  <div className="flex gap-3">
                    <Button data-testid="reject-btn" variant="outline" onClick={() => decide("reject")} disabled={acting} className="flex-1 border-rose-200 text-rose-600 hover:bg-rose-50">
                      {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><X className="h-4 w-4 mr-1.5" /> Rejeter</>}
                    </Button>
                    <Button data-testid="approve-btn" onClick={() => decide("approve")} disabled={acting} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                      {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Valider le paiement</>}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border-t border-slate-100 pt-4 mt-2 flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <ShieldAlert className="h-5 w-5 shrink-0" />
                  Seul l'Administrateur Principal Central peut valider ou rejeter ce paiement.
                </div>
              )
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Payments() {
  const { user } = useAuth();
  const isPrincipal = user?.role === "principal_admin";
  const [params] = useSearchParams();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("pending");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => api.get("/payments").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);

  if (!items) return <Spinner />;
  const filtered = tab === "all" ? items : items.filter((p) => p.status === tab);
  const counts = { pending: items.filter((p) => p.status === "pending").length };

  return (
    <div>
      <PageHeader
        title="Paiements commerciaux"
        subtitle={isPrincipal ? "Validez ou rejetez les paiements. Vous etes le seul decideur." : "Suivi des paiements. La validation est reservee a l'Administrateur Principal."}
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="pending" data-testid="tab-pending">En attente {counts.pending > 0 && <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[10px] text-white">{counts.pending}</span>}</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Valides</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejetes</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">Tous</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <EmptyState icon={CreditCard} title="Aucun paiement" hint="Les paiements apparaitront ici." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="px-6 py-4">Facture</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Methode</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Justificatif</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right px-6">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} data-testid={`table-row-${p.id}`} className="cursor-pointer" onClick={() => setOpenId(p.id)}>
                  <TableCell className="px-6 py-4 font-mono text-sm">{p.invoice_number}</TableCell>
                  <TableCell className="font-medium text-slate-800">{p.customer_name}</TableCell>
                  <TableCell><MethodBadge method={p.method} /></TableCell>
                  <TableCell className="text-right font-mono font-semibold">{formatFCFA(p.amount)}</TableCell>
                  <TableCell>{p.proof_file_id ? <span className="text-xs text-teal-600">Joint</span> : p.method === "cash" ? <span className="text-xs text-slate-400 font-mono">{p.internal_receipt_number}</span> : "-"}</TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-right px-6">
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setOpenId(p.id); }} data-testid={`view-payment-${p.id}`}>
                      {p.status === "pending" && isPrincipal ? "Traiter" : "Details"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <PaymentDialog paymentId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
